// Project contacts roster — reusable in the project wizard (buffer mode) and on
// Project Detail (live CRUD). Subcontractors are info-only unless access ticked.
// Existing contacts are editable inline (pencil → edit fields → save/cancel).
import { useEffect, useState } from "react";
import { Users, Plus, Trash2, Mail, Phone, ShieldCheck, Info, Loader2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const FALLBACK_ROLES = [
    "Managing Director", "Client Admin", "Operations Manager", "Contracts Manager",
    "Site Manager", "Cover Site Manager", "Principal Designer", "Principal Contractor", "Subcontractor",
];
const INFO_ONLY_DEFAULT = new Set(["Subcontractor"]);
const blank = { role: "Site Manager", name: "", email: "", phone: "", organisation: "", access_required: false, notes: "" };

function effectiveInfoOnly(role, access_required) {
    return INFO_ONLY_DEFAULT.has(role) && !access_required;
}

const inputCls = "h-9 px-2.5 rounded-lg border border-[#E5E0DA] bg-white text-sm";

// Shared editable fields grid, used by both the "add" form and inline "edit".
function ContactFields({ form, set, roles, idPrefix }) {
    return (
        <>
            <div className="grid sm:grid-cols-2 gap-2">
                <select data-testid={`${idPrefix}-role-input`} value={form.role} onChange={(e) => set("role", e.target.value)} className={inputCls}>
                    {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <input data-testid={`${idPrefix}-name-input`} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" className={inputCls} />
                <input data-testid={`${idPrefix}-email-input`} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" className={inputCls} />
                <input data-testid={`${idPrefix}-phone-input`} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" className={inputCls} />
                <input data-testid={`${idPrefix}-org-input`} value={form.organisation} onChange={(e) => set("organisation", e.target.value)} placeholder="Organisation" className={inputCls} />
                <input data-testid={`${idPrefix}-notes-input`} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes" className={inputCls} />
            </div>
            <label className="flex items-center gap-1.5 text-[11.5px] mt-2.5" style={{ color: "#3e4b63" }}>
                <input data-testid={`${idPrefix}-access-input`} type="checkbox" checked={form.access_required} onChange={(e) => set("access_required", e.target.checked)} />
                Access required (grants them portal access to this project if they already have a login)
                {INFO_ONLY_DEFAULT.has(form.role) && !form.access_required && <span className="text-[10.5px]" style={{ color: "#64718a" }}>· stored info-only</span>}
            </label>
        </>
    );
}

export default function ProjectContactsEditor({ projectId = null, value = [], onChange, onChanged, canEdit = true }) {
    const [roles, setRoles] = useState(FALLBACK_ROLES);
    const [list, setList] = useState(projectId ? null : value);
    const [form, setForm] = useState(blank);
    const [busy, setBusy] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [editForm, setEditForm] = useState(blank);
    const [savingEdit, setSavingEdit] = useState(false);

    useEffect(() => { api.get("/projects/meta/contact-roles").then((r) => setRoles(r.data.roles || FALLBACK_ROLES)).catch(() => {}); }, []);

    const reload = () => {
        if (!projectId) return;
        api.get(`/projects/${projectId}/contacts`).then((r) => setList(r.data)).catch(() => setList([]));
    };
    useEffect(() => { if (projectId) reload(); }, [projectId]);

    const rows = projectId ? (list || []) : value;
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setEdit = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));

    function normalise(f) {
        return {
            role: f.role, name: f.name.trim(),
            email: f.email.trim() || null, phone: f.phone.trim(),            organisation: f.organisation.trim(),
            access_required: !!f.access_required,
            info_only: effectiveInfoOnly(f.role, f.access_required),
            notes: f.notes.trim(),
        };
    }

    async function add() {
        if (!form.name.trim()) { toast.error("Contact name is required"); return; }
        const payload = normalise(form);
        if (projectId) {
            setBusy(true);
            try {
                await api.post(`/projects/${projectId}/contacts`, payload);
                toast.success("Contact added");
                setForm(blank); setShowAdd(false); reload(); onChanged?.();
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add contact"); }
            finally { setBusy(false); }
        } else {
            onChange?.([...(value || []), { ...payload, _tmp: Date.now() }]);
            setForm(blank); setShowAdd(false);
        }
    }

    function startEdit(c) {
        setEditingKey(c.id || c._tmp);
        setEditForm({
            role: c.role || "Site Manager", name: c.name || "", email: c.email || "",
            phone: c.phone || "", organisation: c.organisation || "",
            access_required: !!c.access_required, notes: c.notes || "",
        });
    }
    function cancelEdit() { setEditingKey(null); setEditForm(blank); }

    async function saveEdit(c, i) {
        if (!editForm.name.trim()) { toast.error("Contact name is required"); return; }
        const payload = normalise(editForm);
        if (projectId) {
            setSavingEdit(true);
            try {
                await api.patch(`/projects/${projectId}/contacts/${c.id}`, payload);
                toast.success("Contact updated");
                cancelEdit(); reload(); onChanged?.();
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update contact"); }
            finally { setSavingEdit(false); }
        } else {
            onChange?.(value.map((row, idx) => (idx === i ? { ...row, ...payload } : row)));
            cancelEdit();
        }
    }

    async function remove(c, i) {
        if (projectId) {
            try { await api.delete(`/projects/${projectId}/contacts/${c.id}`); reload(); onChanged?.(); }
            catch { toast.error("Could not remove contact"); }
        } else {
            onChange?.(value.filter((_, idx) => idx !== i));
        }
    }

    return (
        <div data-testid="project-contacts-editor">
            <div className="flex items-center justify-between gap-2 mb-2.5">
                <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                    <span className="text-[12.5px] font-bold text-[#1F1F1F]">Project contacts</span>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#F0EDE8]" style={{ color: "#3e4b63" }}>{rows.length}</span>
                </div>
                {canEdit && (
                    <button data-testid="contact-add-open-button" onClick={() => { setForm(blank); setShowAdd(true); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        <Plus className="w-3.5 h-3.5" /> Add contact
                    </button>
                )}
            </div>

            {list === null && projectId ? (
                <div className="text-[12px] italic py-2" style={{ color: "#64718a" }}><Loader2 className="inline w-3 h-3 animate-spin mr-1" />Loading…</div>
            ) : rows.length === 0 ? (
                <div className="text-[12px] italic py-2" style={{ color: "#64718a" }}>No contacts yet.</div>
            ) : (
                <ul className="space-y-1.5 mb-3">
                    {rows.map((c, i) => {
                        const key = c.id || c._tmp || i;
                        const isEditing = editingKey != null && (editingKey === c.id || editingKey === c._tmp);
                        if (isEditing && canEdit) {
                            return (
                                <li key={key} data-testid={`project-contact-edit-${c.id || i}`}
                                    className="rounded-[10px] border p-3" style={{ borderColor: "#C75A1B", background: "#FBF8F3" }}>
                                    <ContactFields form={editForm} set={setEdit} roles={roles} idPrefix="edit-contact" />
                                    <div className="flex items-center justify-end gap-2 mt-2.5">
                                        <button data-testid={`project-contact-edit-cancel-${c.id || i}`} onClick={cancelEdit}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11.5px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F]">
                                            <X className="w-3.5 h-3.5" /> Cancel
                                        </button>
                                        <button data-testid={`project-contact-edit-save-${c.id || i}`} onClick={() => saveEdit(c, i)} disabled={savingEdit}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11.5px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                            {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
                                        </button>
                                    </div>
                                </li>
                            );
                        }
                        return (
                            <li key={key} data-testid={`project-contact-${c.id || i}`}
                                className="flex items-center gap-3 px-3 py-2 rounded-[9px] border" style={{ borderColor: "#EFEAE3" }}>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate flex items-center gap-2">
                                        {c.name}
                                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: "#E7EEF9", color: "#2A5DB0" }}>{c.role}</span>
                                        {c.access_required ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: "#E8F1E6", color: "#3F7D3A" }}><ShieldCheck className="w-3 h-3" />Access</span>
                                        ) : c.info_only ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: "#F0EDE8", color: "#64718a" }}><Info className="w-3 h-3" />Info only</span>
                                        ) : null}
                                    </div>
                                    <div className="text-[10.5px] flex items-center gap-3 flex-wrap mt-0.5" style={{ color: "#3e4b63" }}>
                                        {c.organisation && <span>{c.organisation}</span>}
                                        {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                                        {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                                        {c.notes && <span className="italic" style={{ color: "#64718a" }}>{c.notes}</span>}
                                    </div>
                                </div>
                                {canEdit && (
                                    <div className="flex items-center gap-1">
                                        <button data-testid={`project-contact-edit-btn-${c.id || i}`} onClick={() => startEdit(c)} title="Edit contact"
                                            className="text-[#6F6A64] p-1 rounded hover:bg-[#F0EDE8] hover:text-[#1F1F1F]"><Pencil className="w-3.5 h-3.5" /></button>
                                        <button data-testid={`project-contact-remove-${c.id || i}`} onClick={() => remove(c, i)} title="Remove contact"
                                            className="text-[#B33A2E] p-1 rounded hover:bg-[#FBE8E5]"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {canEdit && showAdd && (
                <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) setShowAdd(false); }}>
                    <div data-testid="contact-add-dialog" className="bg-white rounded-[14px] shadow-xl w-full max-w-[560px] border" style={{ borderColor: "#E5E0DA" }}>
                        <div className="px-5 pt-4 pb-3 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                            <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                                <h3 className="text-[15px] font-bold text-[#1F1F1F]">Add project contact</h3>
                            </div>
                            <button data-testid="contact-add-dialog-close" onClick={() => setShowAdd(false)} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="p-5" style={{ background: "#FBF8F3" }}>
                            <ContactFields form={form} set={set} roles={roles} idPrefix="contact" />
                        </div>
                        <div className="px-5 py-3 border-t flex items-center justify-end gap-2" style={{ borderColor: "#F0EDE8" }}>
                            <button data-testid="contact-add-cancel" onClick={() => setShowAdd(false)}
                                className="px-3 py-1.5 rounded-[8px] text-[11.5px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                            <button data-testid="contact-add-button" onClick={add} disabled={busy}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11.5px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add contact
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
